import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import axios from 'axios'

// ** MUI Components
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import CardContent from '@mui/material/CardContent'
import Card from '@mui/material/Card'
import InputAdornment from '@mui/material/InputAdornment'
import CustomTextField from '../../../components/mui/text-field'
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';

import AuthIllustrationV1Wrapper from '../login/AuthIllustrationV1Wrapper.js'
import BlankLayout from '../../Layout/blankLayout.js'

// ** Icon Imports
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import LinearProgress from '@mui/material/LinearProgress'
import useAuth from '../../../hooks/useAuth.js'

import { dev_baseUrl } from "../../../utils/var";

// const linkStyles = () => ({
//   textDecoration: 'none',
//   color: '#007AFF',
//   fontWeight: 500,
//   fontSize: '14px',
//   px: 1,
//   cursor: 'pointer',
//   '&:hover': {
//     textDecoration: 'underline',
//   }
// })

const ResetPassword = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [values, setValues] = useState({
    newPassword: '',
    showNewPassword: false,
    confirmNewPassword: '',
    showConfirmNewPassword: false,
  })

  const [userId, setUserId] = useState('')
  const [token, setToken] = useState('')
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false);
  const { login, logout } = useAuth();
  
  useEffect(() => {
    logout();
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const timer = setTimeout(() => {
      
        setErrors({});
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [errors]);

  useEffect(() => {
    const query = new URLSearchParams(location.search)
    setUserId(query.get('userId') || '')
    setToken(encodeURIComponent(query.get('token')) || '')
    
  }, [location.search])

  const handleNewPasswordChange = prop => event => {
    setValues({ ...values, [prop]: event.target.value });
    setErrors({ ...errors, [prop]: '' });
  }

  const handleClickShowNewPassword = () => {
    setValues({ ...values, showNewPassword: !values.showNewPassword })
  }

  const handleConfirmNewPasswordChange = prop => event => {
    setValues({ ...values, [prop]: event.target.value })
  }

  const handleClickShowConfirmNewPassword = () => {
    setValues({ ...values, showConfirmNewPassword: !values.showConfirmNewPassword })
  }
  
  const validatePassword = password => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()_\-+=<>.,])[A-Za-z\d@$!%*?&#^()_\-+=<>.,]{8,}$/;
    return regex.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    // setErrors({})
    const newErrors = {};

    if (values.newPassword !== values.confirmNewPassword) {
      newErrors.password2 = 'Passwords do not match'
      // setErrors('Passwords do not match')      
    }
    if (!values.newPassword ) {
      newErrors.password1 = 'Please enter valid password'      
    }
    if (!values.confirmNewPassword ) {
      newErrors.password2 = 'Please enter valid confirm password'      
    }
    if (values.newPassword && !validatePassword(values.newPassword)) {
      // setValues({ ...values, errorPassword: "Password does not meet criteria" });
      // return;
      newErrors.password1 = "Password does not meet criteria";
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${dev_baseUrl}/Account/resetpasswordconfirm`, {
        userId: userId,
        token: token,
        newPassword: values.newPassword,
      })

      if (!response.data.isError) {
        navigate('/login', { state: { message: 'Password Reset Successful, please login to continue' } })
      } else {
        setErrors({message: response.data.message})
      }
    } catch (err) {
      setErrors({message: 'An error occurred. Please try again.'})
    } finally {
      setLoading(false);
    }
  }

  const checkPasswordGuidelines = (password) => {
    return {
      length: password.length >= 8,
      upperCase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    }
  }

  const passwordGuidelines = checkPasswordGuidelines(values.newPassword);
  
  const calculatePasswordStrength = (guidelines) => {
    const criteria = Object.values(guidelines).filter(value => value).length;
    return (criteria / 4) * 100;
  };

  const passwordStrength = calculatePasswordStrength(passwordGuidelines);

  const getColor = (passwordStrength) => {
    if (passwordStrength === 100) {
      return 'green';
    } else if (passwordStrength >= 75) {
      return 'orange';
    } else if (passwordStrength >= 50) {
      return 'orange';
    } else {
      return 'red';
    }
  };


  return (
    <BlankLayout>
      <Box className='content-center'>
        <AuthIllustrationV1Wrapper>
          <Card variant='outlined'>
            <CardContent sx={{ p: theme => `${theme.spacing(10.5, 8, 8)} !important` }}>
              <Box sx={{ mb: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img height='45' alt='error-illustration' src='/images/logo2.svg' />
              </Box>
              <Box sx={{ mb: 6, textAlign: 'center' }}>
                <Typography variant='h5' sx={{ mb: 1.5 }}>
                  Reset Password
                </Typography>
              </Box>
              <form noValidate autoComplete='off' onSubmit={handleSubmit}>
                <CustomTextField
                  fullWidth
                  autoFocus
                  label='New Password'
                  placeholder='············'
                  value={values.newPassword}
                  sx={{ display: 'flex', mb: 4 }}
                  id='auth-reset-password-new-password'
                  onChange={handleNewPasswordChange('newPassword')}
                  type={values.showNewPassword ? 'text' : 'password'}
                  error={!!errors.password1}
                  helperText={errors.password1}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          edge='end'
                          onClick={handleClickShowNewPassword}
                          onMouseDown={e => e.preventDefault()}
                          aria-label='toggle password visibility'
                        >
                          {values.showNewPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <CustomTextField
                  fullWidth
                  label='Confirm Password'
                  placeholder='············'
                  sx={{ display: 'flex', mb: 1.5 }}
                  value={values.confirmNewPassword}
                  id='auth-reset-password-confirm-password'
                  type={values.showConfirmNewPassword ? 'text' : 'password'}
                  onChange={handleConfirmNewPasswordChange('confirmNewPassword')}
                  error={!!errors.password2}
                  helperText={errors.password2}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          edge='end'
                          onMouseDown={e => e.preventDefault()}
                          aria-label='toggle password visibility'
                          onClick={handleClickShowConfirmNewPassword}
                        >
                          {values.showConfirmNewPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <Box sx={{ my: 2 }}>
                  <LinearProgress value={passwordStrength} variant="determinate"
                  sx={{
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getColor(passwordStrength)
                    },
                    height: '7px'
                  }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontSize: '12px' }}>Must contain at least:</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.75 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      {passwordGuidelines.upperCase ? (
                        <CheckCircleIcon sx={{ color: '#1FC16B', height: '17px', width: '17px', mr: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#99A0AE', height: '17px', width: '17px', mr: 1 }} />
                      )}
                      <Typography sx={{ fontSize: '12px' }}> 1 upper case</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      {passwordGuidelines.number ? (
                        <CheckCircleIcon sx={{ color: '#1FC16B', height: '17px', width: '17px', mr: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#99A0AE', height: '17px', width: '17px', mr: 1 }} />
                      )}
                      <Typography sx={{ fontSize: '12px' }}> 1 number</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      {passwordGuidelines.specialChar ? (
                        <CheckCircleIcon sx={{ color: '#1FC16B', height: '17px', width: '17px', mr: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#99A0AE', height: '17px', width: '17px', mr: 1 }} />
                      )}
                      <Typography sx={{ fontSize: '12px' }}> 1 special character</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      {passwordGuidelines.length ? (
                        <CheckCircleIcon sx={{ color: '#1FC16B', height: '17px', width: '17px', mr: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#99A0AE', height: '17px', width: '17px', mr: 1 }} />
                      )}
                      <Typography sx={{ fontSize: '12px' }}> 8 characters</Typography>
                    </Box>
                  </Box>
                </Box>
                {errors && <Typography color='error' sx={{ mb: 2 }}>{errors.message}</Typography>}
                <Button disabled={loading} fullWidth type='submit' variant='contained' color='main'>
                  Save and Continue         
                </Button>
              </form>
            </CardContent>
          </Card>
        </AuthIllustrationV1Wrapper>
      </Box>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </BlankLayout>
  )
}

export default ResetPassword
